import { colors } from "@/lib/design-tokens";
import {
  MetricSpec,
  MetricGroupData,
  ObjectiveBucket,
  VisibilityState,
  MetricGroup,
} from "@/types/metrics";

// Inline metrics spec from CSV for frontend bundle
// In a production app, this could be fetched from an API endpoint
const METRICS_SPEC: MetricSpec[] = [
  {
    metricGroup: "Delivery",
    metricName: "spend",
    displayName: "Spend",
    isUniversal: true,
    awarenessVisibility: "always",
    considerationVisibility: "always",
    conversionVisibility: "always",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Total budget spent delivering this ad",
      answersQuestion: "How much did this creative cost?",
      watchOut: "Spend alone doesn't indicate performance",
    },
  },
  {
    metricGroup: "Delivery",
    metricName: "impressions",
    displayName: "Impressions",
    isUniversal: true,
    awarenessVisibility: "highlight",
    considerationVisibility: "collapsed",
    conversionVisibility: "always",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Number of times the ad was displayed on screen",
      answersQuestion: "How much distribution did this creative get?",
      watchOut: "High impressions don't guarantee impact",
    },
  },
  {
    metricGroup: "Delivery",
    metricName: "reach",
    displayName: "Reach",
    isUniversal: true,
    awarenessVisibility: "highlight",
    considerationVisibility: "collapsed",
    conversionVisibility: "hidden",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Number of unique users who saw the ad at least once",
      answersQuestion: "How many different people did this creative reach?",
      watchOut: "Reach quality matters more than reach volume",
    },
  },
  {
    metricGroup: "Delivery",
    metricName: "frequency",
    displayName: "Frequency",
    isUniversal: true,
    awarenessVisibility: "highlight",
    considerationVisibility: "highlight",
    conversionVisibility: "highlight",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Average number of times each user saw the ad",
      answersQuestion: "Are we over-exposing the same audience?",
      watchOut: "High frequency causes ad fatigue and inflates CPM",
    },
  },
  {
    metricGroup: "Delivery",
    metricName: "cpm",
    displayName: "CPM",
    isUniversal: true,
    awarenessVisibility: "highlight",
    considerationVisibility: "collapsed",
    conversionVisibility: "collapsed",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Cost per 1,000 impressions served",
      answersQuestion: "How efficiently am I buying reach?",
      watchOut: "Cheap CPM doesn't guarantee quality audiences",
    },
  },
  {
    metricGroup: "Traffic",
    metricName: "clicks",
    displayName: "All Clicks",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "collapsed",
    conversionVisibility: "hidden",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "All click interactions on the ad, including profile clicks and reactions",
      answersQuestion: "How much total click activity is this creative generating?",
      watchOut: "Includes low-quality clicks like profile taps",
    },
  },
  {
    metricGroup: "Traffic",
    metricName: "linkClicks",
    displayName: "Link Clicks",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "highlight",
    conversionVisibility: "collapsed",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Clicks that take users to your chosen destination (website, app, etc.)",
      answersQuestion: "Is this creative driving intentional traffic?",
      watchOut: "Clickbait creative inflates clicks without real intent",
    },
  },
  {
    metricGroup: "Traffic",
    metricName: "outboundClicks",
    displayName: "Outbound Clicks",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "highlight",
    conversionVisibility: "collapsed",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Clicks that take users off Meta's platform entirely",
      answersQuestion: "Are people actually leaving Meta to visit my site?",
      watchOut: "Lower volume but a cleaner intent signal than link clicks",
    },
  },
  {
    metricGroup: "Traffic",
    metricName: "ctr",
    displayName: "CTR (All)",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "collapsed",
    conversionVisibility: "hidden",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Percentage of impressions that resulted in any click",
      answersQuestion: "What share of viewers interacted with this ad?",
      watchOut: "Inflated by engagement clicks (likes, comments) — not just destination clicks",
    },
  },
  {
    metricGroup: "Traffic",
    metricName: "linkCtr",
    displayName: "Link CTR",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "highlight",
    conversionVisibility: "hidden",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Percentage of impressions that resulted in a link click to your destination",
      answersQuestion: "How compelling is my call-to-action?",
      watchOut: "Drops quickly with audience fatigue at high frequency",
    },
  },
  {
    metricGroup: "Traffic",
    metricName: "outboundCtr",
    displayName: "Outbound CTR",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "highlight",
    conversionVisibility: "hidden",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Percentage of impressions that resulted in a click off Meta's platform",
      answersQuestion: "How effective is this creative at driving off-platform traffic?",
      watchOut: "Varies by platform placement (Feed vs Stories vs Reels)",
    },
  },
  {
    metricGroup: "Traffic",
    metricName: "cpc",
    displayName: "CPC",
    isUniversal: false,
    awarenessVisibility: "hidden",
    considerationVisibility: "highlight",
    conversionVisibility: "collapsed",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Average cost for each click interaction on the ad",
      answersQuestion: "How cheaply am I driving clicks?",
      watchOut: "Low CPC can mask poor traffic quality",
    },
  },
  {
    metricGroup: "Traffic",
    metricName: "costPerLinkClick",
    displayName: "Cost per Link Click",
    isUniversal: false,
    awarenessVisibility: "hidden",
    considerationVisibility: "highlight",
    conversionVisibility: "collapsed",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Average cost for each click to your destination",
      answersQuestion: "How efficiently am I driving intentional traffic?",
      watchOut: "Heavily influenced by CTR — creative quality matters more than bid",
    },
  },
  {
    metricGroup: "Traffic",
    metricName: "landingPageViews",
    displayName: "Landing Page Views",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "collapsed",
    conversionVisibility: "highlight",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Number of times users clicked and the landing page fully loaded",
      answersQuestion: "Are my clicks turning into actual site visits?",
      watchOut: "Slow page load times will suppress this metric",
    },
  },
  {
    metricGroup: "Traffic",
    metricName: "costPerLandingPageView",
    displayName: "Cost per LPV",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "collapsed",
    conversionVisibility: "highlight",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Average cost for each fully loaded landing page view",
      answersQuestion: "How much does a real site visit cost me?",
      watchOut: "Depends on both creative quality and site load speed",
    },
  },
  {
    metricGroup: "Engagement",
    metricName: "postEngagements",
    displayName: "Post Engagements",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "collapsed",
    conversionVisibility: "hidden",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Total interactions including reactions, comments, shares, and clicks",
      answersQuestion: "How much total engagement is this creative generating?",
      watchOut: "Dominated by low-intent reactions — weight shares and comments higher",
    },
  },
  {
    metricGroup: "Engagement",
    metricName: "reactions",
    displayName: "Reactions",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "collapsed",
    conversionVisibility: "hidden",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Likes, loves, and other emoji reactions on the ad",
      answersQuestion: "Is the creative generating emotional responses?",
      watchOut: "Weak intent signal — easy to give, doesn't predict conversion",
    },
  },
  {
    metricGroup: "Engagement",
    metricName: "comments",
    displayName: "Comments",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "collapsed",
    conversionVisibility: "hidden",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "User comments and discussions on the ad post",
      answersQuestion: "Is this creative sparking conversation?",
      watchOut: "Can skew negative — check sentiment, not just count",
    },
  },
  {
    metricGroup: "Engagement",
    metricName: "shares",
    displayName: "Shares",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "collapsed",
    conversionVisibility: "hidden",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Number of times users shared this ad with others",
      answersQuestion: "Is this creative worth sharing?",
      watchOut: "Rare but a very strong signal of creative resonance",
    },
  },
  {
    metricGroup: "Engagement",
    metricName: "costPerEngagement",
    displayName: "Cost per Engagement",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "collapsed",
    conversionVisibility: "hidden",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Average cost for each engagement (reaction, comment, share, or click)",
      answersQuestion: "How efficiently is this creative generating engagement?",
      watchOut: "Biased by campaign objective — engagement campaigns naturally optimize for this",
    },
  },
  {
    metricGroup: "Video",
    metricName: "videoViews",
    displayName: "Video Views",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "collapsed",
    conversionVisibility: "hidden",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Number of times the video was played (includes very short views)",
      answersQuestion: "How many times did the video start playing?",
      watchOut: "Very low threshold — auto-play counts, so this overstates true interest",
    },
  },
  {
    metricGroup: "Video",
    metricName: "thruplays",
    displayName: "ThruPlays",
    isUniversal: false,
    awarenessVisibility: "highlight",
    considerationVisibility: "collapsed",
    conversionVisibility: "hidden",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Views of 15 seconds or the full video, whichever is shorter",
      answersQuestion: "How many people watched a meaningful amount?",
      watchOut: "Shorter videos naturally get more ThruPlays — compare within similar lengths",
    },
  },
  {
    metricGroup: "Video",
    metricName: "costPerThruplay",
    displayName: "Cost per ThruPlay",
    isUniversal: false,
    awarenessVisibility: "highlight",
    considerationVisibility: "collapsed",
    conversionVisibility: "hidden",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Average cost for each ThruPlay (15s or full view)",
      answersQuestion: "How efficiently am I buying meaningful video views?",
      watchOut: "Biased by video length — shorter videos have cheaper ThruPlays",
    },
  },
  {
    metricGroup: "Video",
    metricName: "video3sViews",
    displayName: "3s Views",
    isUniversal: false,
    awarenessVisibility: "highlight",
    considerationVisibility: "collapsed",
    conversionVisibility: "hidden",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Number of times the video was watched for at least 3 seconds",
      answersQuestion: "Is the opening strong enough to stop the scroll?",
      watchOut: "Measures hook power only — doesn't indicate retention beyond 3 seconds",
    },
  },
  {
    metricGroup: "Video",
    metricName: "video10sViews",
    displayName: "10s Views",
    isUniversal: false,
    awarenessVisibility: "highlight",
    considerationVisibility: "collapsed",
    conversionVisibility: "hidden",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Number of times the video was watched for at least 10 seconds",
      answersQuestion: "Does the creative sustain attention past the first moments?",
      watchOut: "Heavily influenced by creative pacing and hook-to-story transition",
    },
  },
  {
    metricGroup: "Video",
    metricName: "video25Percent",
    displayName: "25% Views",
    isUniversal: false,
    awarenessVisibility: "highlight",
    considerationVisibility: "highlight",
    conversionVisibility: "collapsed",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Number of times the video was watched to 25% of its duration",
      answersQuestion: "Are viewers staying past the opening act?",
      watchOut: "Strongly influenced by hook quality — weak hooks mean low 25% views",
    },
  },
  {
    metricGroup: "Video",
    metricName: "video50Percent",
    displayName: "50% Views",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "highlight",
    conversionVisibility: "collapsed",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Number of times the video was watched to 50% of its duration",
      answersQuestion: "Is the middle of my video holding attention?",
      watchOut: "Longer videos naturally have lower 50% rates — compare within similar lengths",
    },
  },
  {
    metricGroup: "Video",
    metricName: "video75Percent",
    displayName: "75% Views",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "collapsed",
    conversionVisibility: "collapsed",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Number of times the video was watched to 75% of its duration",
      answersQuestion: "Is the creative compelling enough to keep most viewers?",
      watchOut: "A rare but strong signal — high 75% views indicate exceptional content",
    },
  },
  {
    metricGroup: "Video",
    metricName: "video95Percent",
    displayName: "95% Views",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "collapsed",
    conversionVisibility: "collapsed",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Number of times the video was watched to 95% of its duration",
      answersQuestion: "Are viewers watching almost the entire video?",
      watchOut: "Skewed by short videos — a 6-second ad reaching 95% is very different from a 60-second one",
    },
  },
  {
    metricGroup: "Video",
    metricName: "avgWatchTime",
    displayName: "Avg Watch Time",
    isUniversal: false,
    awarenessVisibility: "highlight",
    considerationVisibility: "highlight",
    conversionVisibility: "collapsed",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Average number of seconds users watched the video",
      answersQuestion: "How long are people actually watching?",
      watchOut: "Biased by video length — compare against video duration, not across different lengths",
    },
  },
  {
    metricGroup: "Conversion",
    metricName: "viewContent",
    displayName: "View Content",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "collapsed",
    conversionVisibility: "collapsed",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Number of times users viewed a product page on your website after clicking the ad",
      answersQuestion: "Is this creative driving product interest?",
      watchOut: "Depends on correct pixel/CAPI setup — missing events undercount",
    },
  },
  {
    metricGroup: "Conversion",
    metricName: "addToCart",
    displayName: "Add to Cart",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "collapsed",
    conversionVisibility: "collapsed",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Number of times users added a product to their cart after clicking the ad",
      answersQuestion: "Is this creative generating purchase intent?",
      watchOut: "Cart-to-purchase drop-off is common — track alongside purchases",
    },
  },
  {
    metricGroup: "Conversion",
    metricName: "initiateCheckout",
    displayName: "Initiate Checkout",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "collapsed",
    conversionVisibility: "collapsed",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Number of times users started the checkout flow after clicking the ad",
      answersQuestion: "Are users getting to the point of purchase?",
      watchOut: "Heavily influenced by checkout UX — a bad checkout page hurts this metric, not the creative",
    },
  },
  {
    metricGroup: "Conversion",
    metricName: "purchases",
    displayName: "Purchases",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "collapsed",
    conversionVisibility: "highlight",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Number of completed purchases attributed to this ad",
      answersQuestion: "Is this creative actually driving sales?",
      watchOut: "Low volumes make this noisy — wait for statistical significance before deciding",
    },
  },
  {
    metricGroup: "Conversion",
    metricName: "purchaseValue",
    displayName: "Purchase Value",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "collapsed",
    conversionVisibility: "highlight",
    requiresDerivation: false,
    derivationLogic: null,
    tooltipCopy: {
      description: "Total revenue from purchases attributed to this ad",
      answersQuestion: "How much revenue did this creative generate?",
      watchOut: "Attribution models can over- or under-count — cross-reference with backend data",
    },
  },
  {
    metricGroup: "Conversion",
    metricName: "roas",
    displayName: "ROAS",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "collapsed",
    conversionVisibility: "highlight",
    requiresDerivation: true,
    derivationLogic: "purchaseValue/spend",
    tooltipCopy: {
      description: "Revenue generated per euro spent on this ad",
      formula: "Purchase Value ÷ Spend",
      answersQuestion: "Is this creative profitable?",
      watchOut: "Early ROAS is unreliable — attribution windows need 7+ days to stabilize",
    },
  },
  {
    metricGroup: "Derived",
    metricName: "landingPageViewRate",
    displayName: "LPV Rate",
    isUniversal: false,
    awarenessVisibility: "collapsed",
    considerationVisibility: "collapsed",
    conversionVisibility: "highlight",
    requiresDerivation: true,
    derivationLogic: "landingPageViews/linkClicks",
    tooltipCopy: {
      description: "Percentage of link clicks that resulted in a fully loaded landing page",
      formula: "Landing Page Views ÷ Link Clicks",
      answersQuestion: "Are my clicks turning into real site visits?",
      watchOut: "Slow page load times will lower this — fix site speed before blaming the creative",
    },
  },
  {
    metricGroup: "Derived",
    metricName: "hookRate",
    displayName: "Hook Rate",
    isUniversal: false,
    awarenessVisibility: "highlight",
    considerationVisibility: "collapsed",
    conversionVisibility: "hidden",
    requiresDerivation: true,
    derivationLogic: "video3sViews/impressions",
    tooltipCopy: {
      description: "Percentage of impressions where users watched at least 3 seconds",
      formula: "3s Video Views ÷ Impressions",
      answersQuestion: "Is my creative stopping the scroll?",
      watchOut: "Platform auto-play bias can inflate this — compare across creatives, not absolutes",
    },
  },
  {
    metricGroup: "Derived",
    metricName: "holdRate",
    displayName: "Hold Rate",
    isUniversal: false,
    awarenessVisibility: "highlight",
    considerationVisibility: "highlight",
    conversionVisibility: "collapsed",
    requiresDerivation: true,
    derivationLogic: "video25Percent/video3sViews",
    tooltipCopy: {
      description: "Of those who watched 3 seconds, percentage who completed a ThruPlay (15s or full video)",
      formula: "ThruPlays ÷ 3s Video Views",
      answersQuestion: "Does my creative hold attention after the hook?",
      watchOut: "Depends heavily on hook quality and video length",
    },
  },
];

/**
 * Load metrics spec - returns all 39 metrics.
 */
export function loadMetricsSpec(): MetricSpec[] {
  return METRICS_SPEC;
}

/**
 * Get visibility state for a metric given an objective bucket.
 */
export function getVisibility(
  spec: MetricSpec,
  objective: ObjectiveBucket | null
): VisibilityState {
  if (spec.isUniversal) return "always";
  if (!objective) return "hidden";

  switch (objective) {
    case "Awareness":
      return spec.awarenessVisibility;
    case "Consideration":
      return spec.considerationVisibility;
    case "Performance":
      return spec.conversionVisibility;
    default:
      return "hidden";
  }
}

/**
 * Get metrics visible for a given objective, optionally filtered by visibility state.
 * When mediaType is 'image', excludes the Video group and video-derived metrics (hookRate, holdRate).
 */
export function getVisibleMetrics(
  objective: ObjectiveBucket | null,
  visibilityFilter?: VisibilityState,
  mediaType?: "video" | "image"
): MetricSpec[] {
  return METRICS_SPEC.filter((spec) => {
    // Exclude video-specific metrics for image creatives
    if (mediaType === "image") {
      if (spec.metricGroup === "Video") return false;
      if (spec.metricName === "hookRate" || spec.metricName === "holdRate") return false;
    }

    const vis = getVisibility(spec, objective);
    if (visibilityFilter) {
      return vis === visibilityFilter;
    }
    return vis !== "hidden";
  });
}

/**
 * Get highlighted metric names for an objective.
 */
export function getHighlightedMetricNames(
  objective: ObjectiveBucket | null
): string[] {
  return getVisibleMetrics(objective, "highlight").map((s) => s.metricName);
}

/**
 * Group metrics by metricGroup.
 */
export function groupMetrics(
  metrics: MetricSpec[],
  values: Record<string, number | null>
): MetricGroupData[] {
  const groups = new Map<MetricGroup, MetricGroupData>();

  for (const spec of metrics) {
    if (!groups.has(spec.metricGroup)) {
      groups.set(spec.metricGroup, {
        group: spec.metricGroup,
        metrics: [],
      });
    }

    const value = values[spec.metricName] ?? null;
    groups.get(spec.metricGroup)!.metrics.push({
      ...spec,
      value,
      formattedValue: formatMetricValue(spec.metricName, value),
    });
  }

  // Return in canonical order
  const order: MetricGroup[] = [
    "Delivery",
    "Traffic",
    "Engagement",
    "Video",
    "Conversion",
    "Derived",
  ];
  return order
    .map((g) => groups.get(g))
    .filter((g): g is MetricGroupData => g !== undefined);
}

/**
 * Calculate derived metrics from base metrics.
 */
export function calculateDerivedMetrics(
  baseMetrics: Record<string, number | null>
): Record<string, number | null> {
  const derived: Record<string, number | null> = {};

  // ROAS = purchaseValue / spend
  const spend = baseMetrics.spend;
  const purchaseValue = baseMetrics.purchaseValue;
  derived.roas =
    spend && spend > 0 && purchaseValue != null ? purchaseValue / spend : null;

  // Hook Rate = video3sViews / impressions (clamped to [0, 1])
  const impressions = baseMetrics.impressions;
  const video3s = baseMetrics.video3sViews;
  derived.hookRate =
    impressions && impressions > 0 && video3s != null
      ? Math.min(video3s / impressions, 1)
      : null;

  // Hold Rate = thruplays / video3sViews (clamped to [0, 1])
  // Industry standard: of those who stopped scrolling (3s), how many watched to completion (or 15s)
  const thruplays = baseMetrics.thruplays;
  derived.holdRate =
    video3s && video3s > 0 && thruplays != null
      ? Math.min(thruplays / video3s, 1)
      : null;

  // LPV Rate = landingPageViews / linkClicks (clamped to [0, 1])
  const linkClicks = baseMetrics.linkClicks;
  const lpv = baseMetrics.landingPageViews;
  derived.landingPageViewRate =
    linkClicks && linkClicks > 0 && lpv != null
      ? Math.min(lpv / linkClicks, 1)
      : null;

  return derived;
}

/**
 * Format a metric value for display.
 */
export function formatMetricValue(metricName: string, value: number | null): string {
  if (value === null || value === undefined) return "-";

  // Currency metrics
  if (
    [
      "spend",
      "purchaseValue",
      "cpc",
      "cpm",
      "costPerLinkClick",
      "costPerLandingPageView",
      "costPerThruplay",
      "costPerEngagement",
    ].includes(metricName)
  ) {
    return `€${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  // CTR metrics – Meta API returns as percentage values (not ratios)
  // e.g. 0.0574 means 0.0574% — do NOT multiply by 100
  if (["ctr", "linkCtr", "outboundCtr"].includes(metricName)) {
    return `${value.toFixed(2)}%`;
  }

  // Derived rate metrics – calculated as 0-1 ratios, convert to percentage
  if (["hookRate", "holdRate", "landingPageViewRate"].includes(metricName)) {
    return `${(value * 100).toFixed(2)}%`;
  }

  // ROAS
  if (metricName === "roas") {
    return `${value.toFixed(2)}x`;
  }

  // Time metrics (seconds)
  if (metricName === "avgWatchTime") {
    return `${value.toFixed(1)}s`;
  }

  // Default: integer with commas
  if (Number.isInteger(value)) {
    return value.toLocaleString();
  }

  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

// ============================================================================
// PERCENTILE UTILITIES
// ============================================================================

/** Whether higher values are better for each metric. Used for percentile coloring.
 *  null = neutral (contextual metric where direction isn't inherently good/bad). */
export const HIGHER_IS_BETTER: Record<string, boolean | null> = {
  // Delivery
  spend: null,
  impressions: true,
  reach: true,
  frequency: false,
  cpm: false,
  // Traffic
  clicks: true,
  linkClicks: true,
  outboundClicks: true,
  ctr: true,
  linkCtr: true,
  outboundCtr: true,
  cpc: false,
  costPerLinkClick: false,
  landingPageViews: true,
  costPerLandingPageView: false,
  // Engagement
  postEngagements: true,
  reactions: true,
  comments: true,
  shares: true,
  costPerEngagement: false,
  // Video
  videoViews: true,
  thruplays: true,
  costPerThruplay: false,
  video3sViews: true,
  video10sViews: true,
  video25Percent: true,
  video50Percent: true,
  video75Percent: true,
  video95Percent: true,
  avgWatchTime: true,
  // Conversion
  viewContent: true,
  addToCart: true,
  initiateCheckout: true,
  purchases: true,
  purchaseValue: true,
  // Derived
  roas: true,
  hookRate: true,
  holdRate: true,
  landingPageViewRate: true,
  // Manual rates
  viewRate2s: true,
  viewRate3s: true,
  thruPlayRate: true,
};

/** Get color for percentile bar based on effective percentile tier.
 *  Reuses existing 4-tier scheme from PerformanceContextCard.
 *  When higherIsBetter is null, the metric is neutral (e.g. Spend) — use gray. */
export function getPercentileColor(
  percentile: number,
  higherIsBetter: boolean | null
): string {
  if (higherIsBetter === null) return "#9CA3AF"; // gray-400 — neutral
  const effectivePercentile = higherIsBetter ? percentile : 100 - percentile;
  if (effectivePercentile >= 75) return colors.status.success; // green
  if (effectivePercentile >= 50) return colors.status.warning; // yellow
  if (effectivePercentile >= 25) return "#F97316"; // orange
  return colors.status.danger; // red
}

// ============================================================================
// OPTIMIZATION GOAL LABELS
// ============================================================================

const OPTIMIZATION_GOAL_LABELS: Record<string, string> = {
  THRUPLAY: "ThruPlays",
  REACH: "Reach",
  LINK_CLICKS: "Link Clicks",
  LANDING_PAGE_VIEWS: "Landing Page Views",
  OFFSITE_CONVERSIONS: "Conversions",
  APP_INSTALLS: "App Installs",
  IMPRESSIONS: "Impressions",
  LEAD_GENERATION: "Lead Generation",
  POST_ENGAGEMENT: "Post Engagement",
  VALUE: "Value",
  CONVERSATIONS: "Conversations",
  AD_RECALL_LIFT: "Ad Recall",
  ENGAGED_USERS: "Engaged Users",
  PAGE_LIKES: "Page Likes",
  QUALITY_LEAD: "Quality Lead",
};

/** Get human-readable label for a percentile value, accounting for direction.
 *  When higherIsBetter is null (neutral metric like Spend), show raw percentile. */
export function getPercentileLabel(
  percentile: number,
  higherIsBetter: boolean | null
): string {
  if (higherIsBetter === null) return `${percentile}th pctl`;
  const effective = higherIsBetter ? percentile : 100 - percentile;
  if (effective >= 75) return "Top 25%";
  if (effective >= 50) return "Above avg";
  if (effective >= 25) return "Below avg";
  return "Bottom 25%";
}

/** Build tooltip lines for a percentile bar. */
export function getPercentileTooltip(
  percentile: number,
  higherIsBetter: boolean | null,
  sampleSize: number
): { rank: string; context: string; basis: string } {
  const basis = `Based on ${sampleSize} creatives in this scope`;

  if (higherIsBetter === null) {
    return {
      rank: `${percentile}th percentile`,
      context: "This metric is contextual — higher or lower isn't inherently better",
      basis,
    };
  }

  const effective = higherIsBetter ? percentile : 100 - percentile;
  const rank = getPercentileLabel(percentile, higherIsBetter);
  const context = higherIsBetter
    ? `Higher than ${percentile}% of comparable creatives`
    : `Lower than ${100 - percentile}% of comparable creatives`;
  return { rank: `${rank}: ${effective}th percentile`, context, basis };
}

export function formatOptimizationGoal(
  raw: string | null | undefined
): string {
  if (!raw) return "Manual Upload";
  return (
    OPTIMIZATION_GOAL_LABELS[raw] ||
    raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}
